<template>
  <div class="ls-page">

    <div class="ls-wrap">

      <!-- ===================================================== -->
      <!-- LOADING OVERLAY                                       -->
      <!-- ===================================================== -->
      <transition name="ls-fade">
        <div v-if="sales_being_loaded" class="ls-loading-overlay">
          <div class="ls-loading-card">
            <div class="ls-logo ls-logo--lg">FM</div>
            <div class="ls-loading-text">Loading sales…</div>
            <div class="ls-loading-bar">
              <div class="ls-loading-fill" style="width: 100%"></div>
            </div>
          </div>
        </div>
      </transition>

      <!-- ===================================================== -->
      <!-- HERO HEADER                                           -->
      <!-- ===================================================== -->
      <header class="ls-hero">
        <div class="ls-hero-grid">
          <div class="ls-hero-main">
            <div class="ls-hero-label">Transaction log</div>
            <div class="ls-hero-title">
              Sales
              <transition name="ls-pop">
                <span v-if="filteredSalesCount > 0" class="ls-count-pill">{{ filteredSalesCount }}</span>
              </transition>
            </div>
            <div class="ls-hero-tags">
              <span class="ls-tag is-orange">
                <b-icon icon="cash-stack"></b-icon> ${{ formatCurrency(total()) }} gross
              </span>
              <span v-if="picked_location_id" class="ls-tag is-teal">
                <b-icon icon="geo-alt-fill"></b-icon> {{ getLocationLabel(picked_location_id) }}
              </span>
              <span class="ls-tag">
                <b-icon icon="calendar-event"></b-icon> {{ dateRangeLabel }}
              </span>
              <span v-if="picked_payment_method" class="ls-tag is-teal">
                <b-icon icon="credit-card"></b-icon> {{ paymentMethodLabel(picked_payment_method) }}
              </span>
            </div>
          </div>
          <div class="ls-hero-qr">
            <b-icon icon="bar-chart-line" font-scale="2.6"></b-icon>
            <div>
              <div class="ls-qr-title">Sales overview</div>
              <div class="ls-qr-sub">
                {{ sales.length }} total · {{ refundCount }} refunded
              </div>
            </div>
          </div>
        </div>

        <div class="ls-hero-actions">
          <b-button class="ls-btn-ghost-dark" @click="toggleFilters">
            <b-icon icon="funnel-fill"></b-icon>
            {{ filtersVisible ? 'Hide filters' : 'Show filters' }}
          </b-button>
          <b-button class="ls-btn-ghost-dark" @click="fetch_init" :disabled="sales_being_loaded">
            <b-icon icon="arrow-clockwise"></b-icon> Refresh
          </b-button>
          <download-csv
            v-if="sales.length > 0 && ['admin'].includes(packet.userDetails.userrole)"
            class="btn ls-btn-ghost-dark ls-download-btn"
            :data="get_csv_data()"
            :name="get_csv_filename()"
          >
            <b-icon icon="download"></b-icon> Excel
          </download-csv>
        </div>
      </header>

      <!-- ===================================================== -->
      <!-- SINGLE-ROW FILTER STRIP                               -->
      <!-- ===================================================== -->
      <transition name="ls-fade-up">
        <section v-show="!isMobile || filtersVisible" class="ls-card ls-filterstrip">

          <div class="ls-filters-bar">

            <!-- Date range -->
            <div class="ls-fb-item ls-fb-dates">
              <b-input-group class="ls-fb-inputgroup">
                <b-input-group-prepend>
                  <b-input-group-text><b-icon icon="calendar-range"></b-icon></b-input-group-text>
                </b-input-group-prepend>
                <b-form-input
                  id="dates"
                  :placeholder="[query.startDate, query.endDate].filter(Boolean).join(' → ') || 'Dates'"
                  class="ls-fb-input"
                  autocomplete="off"
                ></b-form-input>
                <b-input-group-append>
                  <b-button class="ls-btn-primary ls-fb-btn" @click="search_dates">Go</b-button>
                </b-input-group-append>
              </b-input-group>
            </div>

            <!-- Invoice / customer search -->
            <div class="ls-fb-item ls-fb-search">
              <b-input-group class="ls-fb-inputgroup">
                <b-input-group-prepend>
                  <b-input-group-text><b-icon icon="search"></b-icon></b-input-group-text>
                </b-input-group-prepend>
                <b-form-input
                  v-model="query.invoice_id"
                  placeholder="Invoice #, name, email or phone…"
                  class="ls-fb-input"
                  @keyup.enter="search_invoice_id"
                  autocomplete="off"
                ></b-form-input>
                <b-input-group-append>
                  <b-button class="ls-btn-primary ls-fb-btn" @click="search_invoice_id">Search</b-button>
                </b-input-group-append>
              </b-input-group>
            </div>

            <!-- Location -->
            <div class="ls-fb-item ls-fb-pill">
              <b-icon icon="geo-alt" class="ls-fb-pill-icon"></b-icon>
              <b-form-select
                :options="getFilteredLocations()"
                v-model="picked_location_id"
                class="ls-fb-select"
              >
                <template #first>
                  <b-form-select-option :value="null">All locations</b-form-select-option>
                </template>
              </b-form-select>
            </div>

            <!-- Payment method -->
            <div class="ls-fb-item ls-fb-pill">
              <b-icon icon="credit-card" class="ls-fb-pill-icon"></b-icon>
              <b-form-select
                :options="filteredPaymentMethods"
                v-model="picked_payment_method"
                @change="onPaymentMethodChange"
                class="ls-fb-select"
              >
                <template #first>
                  <b-form-select-option :value="null">All payment methods</b-form-select-option>
                </template>
              </b-form-select>
            </div>

            <!-- Status -->
            <div class="ls-fb-item ls-fb-pill">
              <b-icon icon="circle-half" class="ls-fb-pill-icon"></b-icon>
              <b-form-select
                :options="combined_status_options"
                v-model="combined_status_filter"
                class="ls-fb-select"
              ></b-form-select>
            </div>

            <!-- Sale type -->
            <div class="ls-fb-item ls-fb-pill">
              <b-icon icon="receipt" class="ls-fb-pill-icon"></b-icon>
              <b-form-select
                :options="sale_type_options"
                v-model="sale_type_filter"
                class="ls-fb-select"
              ></b-form-select>
            </div>

            <!-- Order type -->
            <div class="ls-fb-item ls-fb-pill">
              <b-icon icon="truck" class="ls-fb-pill-icon"></b-icon>
              <b-form-select
                :options="orderTypeOptions"
                v-model="orderTypeFilter"
                class="ls-fb-select"
              ></b-form-select>
            </div>

            <b-button class="ls-btn-ghost ls-fb-reset" @click="resetFilters" title="Reset filters">
              <b-icon icon="arrow-counterclockwise"></b-icon>
            </b-button>
          </div>
        </section>
      </transition>

      <!-- ===================================================== -->
      <!-- STATS STRIP                                           -->
      <!-- ===================================================== -->
      <section v-if="sales.length > 0" class="ls-stats">
        <div class="ls-stat is-total">
          <div class="ls-stat-icon"><b-icon icon="cash-stack"></b-icon></div>
          <div class="ls-stat-body">
            <div class="ls-stat-value">${{ formatCurrency(total()) }}</div>
            <div class="ls-stat-label">Gross total</div>
          </div>
        </div>
        <div class="ls-stat is-count">
          <div class="ls-stat-icon"><b-icon icon="receipt"></b-icon></div>
          <div class="ls-stat-body">
            <div class="ls-stat-value">{{ filteredSalesCount }}</div>
            <div class="ls-stat-label">Invoices shown</div>
          </div>
        </div>
        <div class="ls-stat is-refund">
          <div class="ls-stat-icon"><b-icon icon="arrow-return-left"></b-icon></div>
          <div class="ls-stat-body">
            <div class="ls-stat-value">{{ refundCount }}</div>
            <div class="ls-stat-label">Refunds</div>
          </div>
        </div>
        <div class="ls-stat is-layaway">
          <div class="ls-stat-icon"><b-icon icon="hourglass-split"></b-icon></div>
          <div class="ls-stat-body">
            <div class="ls-stat-value">{{ layawayCount }}</div>
            <div class="ls-stat-label">Layaways</div>
          </div>
        </div>
      </section>

      <!-- ===================================================== -->
      <!-- TABLE — 7 columns                                     -->
      <!-- ===================================================== -->
      <section v-if="sales.length > 0" class="ls-card ls-table-card">
        <div class="ls-table-scroll">
          <table class="ls-table">
            <thead>
              <tr>
                <th class="ls-th" style="min-width: 150px" @click="sortby('invoice_id')">
                  <span>Invoice</span> <span class="ls-sort-icon">↕</span>
                </th>
                <th class="ls-th" style="min-width: 190px" @click="sortby('customer')">
                  <span>Customer</span> <span class="ls-sort-icon">↕</span>
                </th>
                <th class="ls-th" style="min-width: 190px" @click="sortby('location_id')">
                  <span>Location &amp; agent</span> <span class="ls-sort-icon">↕</span>
                </th>
                <th class="ls-th" style="min-width: 190px">
                  Order &amp; delivery
                </th>
                <th class="ls-th" style="min-width: 190px">
                  Payment
                </th>
                <th class="ls-th" style="min-width: 140px" @click="sortby('status_display')">
                  <span>Status</span> <span class="ls-sort-icon">↕</span>
                </th>
                <th class="ls-th ls-th--actions" style="min-width: 200px">Actions</th>
              </tr>
            </thead>
            <tbody>
              <!-- Totals row -->
              <tr class="ls-totals-row">
                <td><span class="ls-totals-label">Gross</span></td>
                <td></td>
                <td></td>
                <td></td>
                <td class="ls-totals-amt">
                  <b-icon icon="cash-stack"></b-icon>
                  <span>${{ formatCurrency(total()) }}</span>
                </td>
                <td></td>
                <td></td>
              </tr>

              <!-- Data rows -->
              <tr
                v-for="(sale, idx) in sales"
                v-if="allowed(sale) && row_part_of_chunk(idx)"
                :key="'sale-' + idx + '-' + sale.invoice_id"
                class="ls-row"
                :class="{
                  'is-refund': sale.transaction_type === 'REFUND',
                  'is-layaway': sale.is_layaway && sale.transaction_type !== 'REFUND',
                  'is-highlight': isPaymentMethodMatch(sale),
                }"
                :style="{ animationDelay: (idx * 0.015) + 's' }"
              >
                <!-- ============ INVOICE ============ -->
                <td class="ls-td">
                  <div class="ls-invoice-cell" :class="invoiceCellClass(sale)">
                    <div class="ls-invoice-id">{{ (sale.invoice_id || '').toUpperCase() }}</div>
                    <div class="ls-invoice-date">{{ sale.datetime }}</div>
                    <div class="ls-invoice-tags">
                      <span v-if="sale.transaction_type === 'REFUND'" class="ls-mini-chip is-danger">
                        REFUND
                      </span>
                      <span v-if="sale.is_layaway && sale.transaction_type !== 'REFUND'" class="ls-mini-chip is-warn">
                        LAYAWAY
                      </span>
                      <span v-if="sale.total_payments > 1" class="ls-mini-chip">
                        {{ sale.payment_index + 1 }}/{{ sale.total_payments }}
                      </span>
                    </div>
                  </div>
                </td>

                <!-- ============ CUSTOMER ============ -->
                <td class="ls-td">
                  <div class="ls-cust-cell">
                    <div class="ls-cust-name">{{ (sale.customer_fullname || 'Walk-in').toUpperCase() }}</div>
                    <div v-if="sale.customer_phone" class="ls-cust-line">
                      <b-icon icon="telephone"></b-icon> {{ sale.customer_phone }}
                    </div>
                    <div v-if="sale.email" class="ls-cust-line is-muted">
                      <b-icon icon="envelope"></b-icon> {{ sale.email }}
                    </div>
                  </div>
                </td>

                <!-- ============ LOCATION & AGENT ============ -->
                <td class="ls-td">
                  <div class="ls-loc-cell">
                    <div class="ls-loc-primary">
                      <b-icon icon="geo-alt-fill"></b-icon>
                      {{ (packet.locationsdict && packet.locationsdict[sale.location_id]) || '—' }}
                    </div>
                    <div v-if="packet.sublocationsdict && packet.sublocationsdict[sale.sublocation_id]" class="ls-loc-secondary">
                      {{ packet.sublocationsdict[sale.sublocation_id] }}
                    </div>
                    <div class="ls-agent-chip">
                      <b-icon icon="person-badge"></b-icon>
                      {{ (sale.username || '—').toUpperCase() }}
                    </div>
                  </div>
                </td>

                <!-- ============ ORDER & DELIVERY ============ -->
                <td class="ls-td">
                  <div class="ls-od-cell">
                    <div v-if="getUniqueOrderTypes(sale).length" class="ls-order-chips">
                      <span
                        v-for="ot in getOrderTypesWithCount(sale).slice(0, 2)"
                        :key="ot.type"
                        class="ls-mini-chip"
                        :class="orderTypeChipClass(ot.type)"
                      >
                        <b-icon :icon="orderTypeIcon(ot.type)"></b-icon>
                        {{ shortOrderType(ot.type) }} · {{ ot.count }}
                      </span>
                      <b-button
                        v-if="getUniqueOrderTypes(sale).length > 2"
                        size="sm"
                        class="ls-mini-btn is-tiny"
                        @click="showOrderTypes(sale)"
                      >
                        +{{ getUniqueOrderTypes(sale).length - 2 }}
                      </b-button>
                    </div>
                    <div v-else class="ls-muted-small">No order types</div>

                    <div class="ls-deliv-line">
                      <template v-if="sale.delivery_dates && sale.delivery_dates.length > 0">
                        <b-button
                          v-if="sale.delivery_dates.length > 1"
                          size="sm"
                          class="ls-mini-btn is-tiny is-teal"
                          @click="showDeliveryDates(sale)"
                        >
                          <b-icon icon="calendar-week"></b-icon>
                          {{ sale.delivery_dates.length }} dates
                        </b-button>
                        <span v-else class="ls-mini-chip is-warn">
                          <b-icon icon="calendar-event"></b-icon> {{ sale.delivery_dates[0] }}
                        </span>
                      </template>
                      <span v-else class="ls-muted-small">No dates</span>
                    </div>
                  </div>
                </td>

                <!-- ============ PAYMENT ============ -->
                <td class="ls-td">
                  <div
                    class="ls-pay-cell"
                    @click="showPaymentHistory(sale)"
                    :title="'View payment history for ' + sale.invoice_id"
                  >
                    <div class="ls-pay-amt" :class="sale.transaction_type === 'REFUND' ? 'is-danger' : 'is-teal'">
                      <template v-if="sale.transaction_type === 'REFUND'">
                        -${{ formatCurrency(sale.display_amount || Math.abs(sale.transaction_amount) || 0) }}
                      </template>
                      <template v-else>
                        ${{ formatCurrency(sale.payment_amount || sale.paid_so_far || 0) }}
                      </template>
                    </div>
                    <div v-if="sale.payment_methods && sale.payment_methods.length > 0" class="ls-methods-cell">
                      <span
                        v-for="(method, index) in getPaymentMethodsDisplay(sale.payment_methods).slice(0, 3)"
                        :key="index"
                        class="ls-mini-chip"
                        :class="getPaymentMethodChipClass(method)"
                      >
                        {{ method }}
                      </span>
                      <span
                        v-if="getPaymentMethodsDisplay(sale.payment_methods).length > 3"
                        class="ls-mini-chip"
                      >
                        +{{ getPaymentMethodsDisplay(sale.payment_methods).length - 3 }}
                      </span>
                    </div>
                    <div v-else class="ls-muted-small">No methods</div>
                    <div v-if="sale.net_total && sale.transaction_type !== 'REFUND'" class="ls-pay-note">
                      Net ${{ formatCurrency(sale.net_total) }}
                    </div>
                  </div>
                </td>

                <!-- ============ STATUS ============ -->
                <td class="ls-td">
                  <div v-if="sale.transaction_type === 'REFUND'" class="ls-status-badge is-danger">
                    REFUNDED
                  </div>
                  <div v-else class="ls-status-badge" :class="statusChipClass(sale)">
                    {{ getStatusDisplay(sale) }}
                    <span class="ls-status-sub">
                      {{ sale.completed ? 'COMPLETED' : 'IN PROGRESS' }}
                    </span>
                  </div>
                </td>

                <!-- ============ ACTIONS ============ -->
                <td class="ls-td ls-td--actions">
                  <div class="ls-actions">
                    <b-button class="ls-mini-btn is-primary" size="sm" @click="navigateToInvoice(idx)">
                      <b-icon icon="box-arrow-up-right"></b-icon> View
                    </b-button>

                    <b-button
                      v-if="!isArchived(sale) && sale.transaction_type !== 'REFUND' && ['admin'].includes(packet.userDetails.userrole)"
                      class="ls-mini-btn"
                      size="sm"
                      @click="archive_invoice(sale.invoice_id)"
                    >
                      <b-icon icon="archive"></b-icon>
                    </b-button>

                    <b-button
                      v-if="isArchived(sale) && ['admin', 'locationadmin', 'locationmanager'].includes(packet.userDetails.userrole)"
                      class="ls-mini-btn is-teal"
                      size="sm"
                      @click="unarchive_invoice(sale.invoice_id)"
                    >
                      <b-icon icon="arrow-counterclockwise"></b-icon>
                    </b-button>

                    <b-button
                      v-if="isArchived(sale) && packet.userDetails.userrole === 'admin'"
                      class="ls-mini-btn is-danger"
                      size="sm"
                      @click="void_invoice(sale.invoice_id)"
                    >
                      <b-icon icon="trash"></b-icon>
                    </b-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Empty state -->
      <div v-else-if="!sales_being_loaded" class="ls-card ls-empty">
        <div class="ls-empty-icon"><b-icon icon="receipt" font-scale="2.4"></b-icon></div>
        <h3>No sales found</h3>
        <p>Try adjusting the date range or filters to see results.</p>
        <b-button class="ls-btn-primary" @click="resetFilters">
          <b-icon icon="arrow-counterclockwise"></b-icon> Reset filters
        </b-button>
      </div>

    </div>

    <!-- ===================================================== -->
    <!-- DELIVERY DATES MODAL                                  -->
    <!-- ===================================================== -->
    <b-modal id="delivery-dates-modal" size="md" hide-footer centered content-class="ls-modal">
      <template #modal-header>
        <div class="ls-modal-title">
          <span class="ls-modal-title-icon"><b-icon icon="calendar-week"></b-icon></span>
          <div>
            <div class="ls-modal-title-main">Delivery dates</div>
            <div class="ls-modal-title-sub" v-if="selectedSale">{{ selectedSale.invoice_id }}</div>
          </div>
        </div>
      </template>

      <div v-if="selectedSale" class="ls-modal-body">
        <div class="ls-modal-summary">
          <div>
            <strong>{{ selectedSale.customer_fullname }}</strong>
            <small>{{ selectedSale.customer_phone }}</small>
          </div>
          <div class="ls-chip is-warn">
            {{ selectedSale.delivery_dates.length }} dates
          </div>
        </div>

        <ul class="ls-date-list">
          <li
            v-for="(date, index) in selectedSale.delivery_dates"
            :key="index"
            class="ls-date-item"
            :class="{ 'is-upcoming': isUpcomingDate(date) }"
          >
            <span class="ls-date-icon"><b-icon icon="truck"></b-icon></span>
            <div class="ls-date-details">
              <div class="ls-date-display">{{ formatDeliveryDate(date) }}</div>
              <div class="ls-date-status" :class="getDateStatusClass(date)">{{ getDateStatus(date) }}</div>
            </div>
            <span class="ls-chip">#{{ index + 1 }}</span>
          </li>
        </ul>

        <div class="ls-modal-foot">
          <span class="ls-foot-note">
            Upcoming: <b>{{ getUpcomingDatesCount(selectedSale.delivery_dates) }}</b> of
            {{ selectedSale.delivery_dates.length }}
          </span>
          <b-button class="ls-btn-ghost" @click="$bvModal.hide('delivery-dates-modal')">Close</b-button>
        </div>
      </div>
    </b-modal>

    <!-- ===================================================== -->
    <!-- ORDER TYPES MODAL                                     -->
    <!-- ===================================================== -->
    <b-modal id="order-types-modal" size="md" hide-footer centered content-class="ls-modal">
      <template #modal-header>
        <div class="ls-modal-title">
          <span class="ls-modal-title-icon"><b-icon icon="box-seam"></b-icon></span>
          <div>
            <div class="ls-modal-title-main">Order types</div>
            <div class="ls-modal-title-sub" v-if="selectedSale">{{ selectedSale.invoice_id }}</div>
          </div>
        </div>
      </template>

      <div v-if="selectedSale" class="ls-modal-body">
        <div class="ls-modal-summary">
          <div>
            <strong>{{ selectedSale.customer_fullname }}</strong>
            <small>{{ selectedSale.customer_phone }}</small>
          </div>
          <div class="ls-chip is-teal">{{ selectedSale.items.length }} items</div>
        </div>

        <ul class="ls-order-list">
          <li
            v-for="(ot, index) in getOrderTypesWithCount(selectedSale)"
            :key="index"
            class="ls-order-item"
            :class="getOrderTypeItemClass(ot.type)"
          >
            <span class="ls-order-icon"><b-icon :icon="orderTypeIcon(ot.type)"></b-icon></span>
            <div class="ls-order-details">
              <div class="ls-order-display">{{ formatOrderType(ot.type) }}</div>
              <div class="ls-order-count">{{ ot.count }} item{{ ot.count > 1 ? 's' : '' }}</div>
            </div>
            <span class="ls-chip">{{ getOrderTypeAbbreviation(ot.type) }}</span>
          </li>
        </ul>

        <div class="ls-modal-foot">
          <span class="ls-foot-note">
            {{ getUniqueOrderTypes(selectedSale).length }} type(s) ·
            {{ selectedSale.items.length }} items
          </span>
          <b-button class="ls-btn-ghost" @click="$bvModal.hide('order-types-modal')">Close</b-button>
        </div>
      </div>
    </b-modal>

    <!-- ===================================================== -->
    <!-- PAYMENT HISTORY MODAL                                 -->
    <!-- ===================================================== -->
    <b-modal id="payment-history-modal" size="lg" hide-footer centered content-class="ls-modal">
      <template #modal-header>
        <div class="ls-modal-title">
          <span class="ls-modal-title-icon"><b-icon icon="credit-card"></b-icon></span>
          <div>
            <div class="ls-modal-title-main">Payment history</div>
            <div class="ls-modal-title-sub" v-if="selectedInvoice">{{ selectedInvoice.invoice_id }}</div>
          </div>
        </div>
      </template>

      <div v-if="selectedInvoice" class="ls-modal-body">
        <div class="ls-pay-modal-head">
          <div class="ls-pay-modal-customer">
            <strong>{{ selectedInvoice.customer_fullname }}</strong>
            <small>
              <template v-if="selectedInvoice.customer_phone">{{ selectedInvoice.customer_phone }}</template>
              <template v-if="selectedInvoice.email"> · {{ selectedInvoice.email }}</template>
            </small>
            <div class="ls-pay-modal-tags">
              <span v-if="selectedInvoice.transaction_type === 'REFUND'" class="ls-chip is-danger">REFUND</span>
              <span v-if="selectedInvoice.is_layaway && selectedInvoice.transaction_type !== 'REFUND'" class="ls-chip is-warn">LAYAWAY</span>
            </div>
          </div>
          <div class="ls-pay-modal-totals">
            <template v-if="selectedInvoice.transaction_type === 'REFUND'">
              <div class="ls-pay-tot-row">
                <span>Original</span>
                <strong>${{ formatCurrency(selectedInvoice.refunded_amount || selectedInvoice.grand_total) }}</strong>
              </div>
              <div class="ls-pay-tot-row is-danger">
                <span>Refunded</span>
                <strong>-$
                  {{ formatCurrency(selectedInvoice.display_amount || Math.abs(selectedInvoice.transaction_amount) || 0) }}
                </strong>
              </div>
            </template>
            <template v-else>
              <div class="ls-pay-tot-row">
                <span>Grand total</span>
                <strong>${{ formatCurrency(selectedInvoice.grand_total) }}</strong>
              </div>
              <div v-if="selectedInvoice.net_total && selectedInvoice.net_total !== selectedInvoice.grand_total" class="ls-pay-tot-row is-teal">
                <span>Net total</span>
                <strong>${{ formatCurrency(selectedInvoice.net_total) }}</strong>
              </div>
              <div class="ls-pay-tot-row is-teal">
                <span>{{ selectedInvoice.is_layaway ? 'Paid so far' : 'Amount paid' }}</span>
                <strong>${{ formatCurrency(getTotalPaid(selectedInvoice)) }}</strong>
              </div>
            </template>
          </div>
        </div>

        <div
          v-if="selectedInvoice.is_layaway && selectedInvoice.transaction_type !== 'REFUND'"
          class="ls-pay-progress"
        >
          <div class="ls-pay-progress-head">
            <span>Payment progress</span>
            <strong>
              {{ Math.round((getTotalPaid(selectedInvoice) / (selectedInvoice.net_total || selectedInvoice.grand_total)) * 100) }}%
            </strong>
          </div>
          <b-progress
            height="8px"
            :value="getTotalPaid(selectedInvoice)"
            :max="selectedInvoice.net_total || selectedInvoice.grand_total"
            variant="success"
          ></b-progress>
          <div class="ls-pay-progress-foot">
            <span class="is-warn">
              Remaining: $
              {{ formatCurrency((selectedInvoice.net_total || selectedInvoice.grand_total) - (getTotalPaid(selectedInvoice) || 0)) }}
            </span>
            <span v-if="selectedInvoice.refunded_amount && selectedInvoice.refunded_amount > 0" class="is-danger">
              Includes ${{ formatCurrency(selectedInvoice.refunded_amount) }} refunds
            </span>
          </div>
        </div>

        <div class="ls-pay-section">
          <div class="ls-pay-section-head">
            <b-icon icon="receipt"></b-icon>
            Payment transactions
            <span class="ls-chip">
              {{ selectedInvoice.payment_details ? selectedInvoice.payment_details.length : 0 }}
            </span>
          </div>

          <ul
            v-if="selectedInvoice.payment_details && selectedInvoice.payment_details.length > 0"
            class="ls-pay-list"
          >
            <li
              v-for="(payment, idx) in selectedInvoice.payment_details"
              :key="idx"
              class="ls-pay-item"
              :class="{ 'is-recent': isRecentPayment(payment.ts) }"
            >
              <span class="ls-pay-icon" :class="'is-' + (payment.value || 'default')">
                {{ getPaymentMethodIcon(payment.value) }}
              </span>
              <div class="ls-pay-item-body">
                <div class="ls-pay-item-method">{{ getPaymentMethodDisplay(payment.value) }}</div>
                <div class="ls-pay-item-date">{{ formatPaymentDate(payment.ts) }}</div>
                <div v-if="payment.store_credit_invoice_id || payment.charged_tax_itself" class="ls-pay-item-tags">
                  <span v-if="payment.store_credit_invoice_id" class="ls-chip is-teal">Store credit</span>
                  <span v-if="payment.charged_tax_itself" class="ls-chip is-warn">Tax included</span>
                </div>
              </div>
              <div
                class="ls-pay-item-amount"
                :class="selectedInvoice.transaction_type === 'REFUND' ? 'is-danger' : 'is-teal'"
              >
                <template v-if="selectedInvoice.transaction_type === 'REFUND'">-</template>
                ${{ formatCurrency(payment.amount) }}
              </div>
            </li>
          </ul>

          <div v-else class="ls-empty-inline">
            <b-icon icon="receipt" font-scale="1.6"></b-icon>
            <p>No payment transactions found</p>
          </div>
        </div>

        <div class="ls-modal-foot">
          <span class="ls-foot-note">Total paid: ${{ formatCurrency(getTotalPaid(selectedInvoice)) }}</span>
          <div class="ls-modal-foot-actions">
            <b-button class="ls-btn-ghost" @click="$bvModal.hide('payment-history-modal')">Close</b-button>
            <b-button class="ls-btn-primary" @click="navigateToSelectedInvoice">
              <b-icon icon="box-arrow-up-right"></b-icon> View full invoice
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import $ from 'jquery'
import Backer from '@/components/globals/Backer'
import Requestor from '@/utilities/Requestor'
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'
import 'vue-good-table/dist/vue-good-table.css'
import { VueGoodTable } from 'vue-good-table'

import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'

import 'lightpick/css/lightpick.css'
import moment from 'moment'
import { BIconFunnelFill } from 'bootstrap-vue'

export default {
  name: "ListSales",
  props: ["showbacker"],
  components: { Backer, VueGoodTable, Multiselect, Loading },
  data: function () {
    return {
      packet: {},
      sales: [],
      sales_being_loaded: false,
      query: {
        startDate: null,
        endDate: null,
        invoice_id: null,
      },
      selectedSale: null,
      selectedInvoice: null,
      picked_location_id: null,
      finance_companies: [],
      orderTypeFilter: "all",
      orderTypeOptions: [
        { value: "all", text: "All order types" },
        { value: "home_delivery", text: "Home delivery" },
        { value: "store_pickup", text: "Store pickup" },
      ],
      payment_methods: [
        { value: "CASH", text: "Cash involved" },
        { value: "CC", text: "Credit card involved" },
        { value: "STORE_CREDIT", text: "Store credit involved" },
        { value: "FINANCE", text: "Finance involved" },
        { value: "TAX_EXEMPT", text: "Tax exempt involved" },
      ],
      combined_status_filter: "all",
      combined_status_options: [
        { value: "all", text: "All statuses" },
        { value: "new", text: "New" },
        { value: "open", text: "Open" },
        { value: "processed", text: "Processed" },
        { value: "partial", text: "Partial" },
        { value: "backorder", text: "Backorder" },
        { value: "delivered", text: "Delivered" },
        { value: "completed", text: "Completed" },
        { value: "archived", text: "Archived" },
      ],
      status_filter: "all",
      status_options: [
        { value: "all", text: "All statuses" },
        { value: "0", text: "New" },
        { value: "1", text: "Open" },
        { value: "2", text: "Processed only" },
        { value: "3", text: "Backorder" },
        { value: "4", text: "Partial" },
        { value: "5", text: "Delivered" },
        { value: "10", text: "Archived" },
      ],
      completion_filter: "all",
      completion_options: [
        { value: "all", text: "All completion" },
        { value: "not_completed", text: "Not completed only" },
        { value: "completed", text: "Completed only" },
      ],
      sale_type_filter: "all",
      sale_type_options: [
        { value: "all", text: "All transactions" },
        { value: "regular", text: "Regular sales only" },
        { value: "layaway", text: "Layaway sales only" },
        { value: "refund", text: "Refunds only" },
      ],
      picked_payment_method: null,
      chunksize: 2000000,
      chunkoptions: [],
      chunk_allowed: null,
      invoice_type_options: [
        { value: "regular", text: "Regular" },
        { value: "layaway", text: "Layaway" },
      ],
      invoice_type: "regular",
      sort_asc: true,
      filtersVisible: false,
      isMobile: false,
    }
  },
  computed: {
    filteredSalesCount() {
      return this.sales.filter((s) => this.allowed(s)).length;
    },
    refundCount() {
      return this.sales.filter((s) => this.allowed(s) && s.transaction_type === "REFUND").length;
    },
    layawayCount() {
      return this.sales.filter((s) => this.allowed(s) && s.is_layaway && s.transaction_type !== "REFUND").length;
    },
    dateRangeLabel() {
      if (this.query.startDate && this.query.endDate) {
        return `${moment(this.query.startDate).format("MMM DD")} → ${moment(this.query.endDate).format("MMM DD")}`;
      }
      if (this.query.invoice_id) return `Search: ${this.query.invoice_id}`;
      return "All time";
    },
    filteredPaymentMethods: function () {
      let availableMethods = [
        { value: "CASH", text: "Cash involved" },
        { value: "CC", text: "Credit card involved" },
        { value: "STORE_CREDIT", text: "Store credit involved" },
        { value: "FINANCE", text: "Finance involved" },
        { value: "TAX_EXEMPT", text: "Tax exempt involved" },
      ];

      const presentMethods = new Set();

      this.sales.forEach(sale => {
        if (sale.payment_methods && Array.isArray(sale.payment_methods)) {
          sale.payment_methods.forEach(payment => {
            if (payment && payment.value) {
              if (payment.value === "cash") presentMethods.add("CASH");
              if (payment.value === "cc") presentMethods.add("CC");
              if (payment.value === "store_credit") presentMethods.add("STORE_CREDIT");
              if (payment.value === "finance") presentMethods.add("FINANCE");
              if (payment.value === "tax_exempt") presentMethods.add("TAX_EXEMPT");

              if (payment.value === "finance" && payment.name && payment.name !== "N/A") {
                presentMethods.add("FINANCE - " + payment.name);
              }
              if (payment.value === "tax_exempt" && payment.name && payment.name !== "N/A") {
                presentMethods.add("TAX_EXEMPT - " + payment.name);
              }
            }
          });
        }
      });

      availableMethods = availableMethods.filter(method => presentMethods.has(method.value));

      this.finance_companies.forEach(fc => {
        const financeOptionValue = "FINANCE - " + fc.finance_company_name;
        if (presentMethods.has(financeOptionValue)) {
          availableMethods.push({
            value: financeOptionValue,
            text: "Finance · " + fc.finance_company_name,
          });
        }
        if (fc.company_type === "tax_exempt") {
          const taxExemptOptionValue = "TAX_EXEMPT - " + fc.finance_company_name;
          if (presentMethods.has(taxExemptOptionValue)) {
            availableMethods.push({
              value: taxExemptOptionValue,
              text: "Tax exempt · " + fc.finance_company_name,
            });
          }
        }
      });

      return availableMethods;
    }
  },
  methods: {
    /* ===========================
       UI HELPERS
    =========================== */
    handleResize() {
      this.isMobile = window.innerWidth < 768
    },
    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },
    resetFilters() {
      this.picked_location_id = null;
      this.picked_payment_method = null;
      this.combined_status_filter = "all";
      this.sale_type_filter = "all";
      this.orderTypeFilter = "all";
      this.query.startDate = null;
      this.query.endDate = null;
      this.query.invoice_id = null;
      this.fetch_init();
    },
    getLocationLabel(value) {
      if (!this.packet.locations) return value;
      const loc = this.packet.locations.find((l) => l.value == value);
      return loc ? loc.text : value;
    },
    paymentMethodLabel(value) {
      if (!value) return "";
      const opt = this.filteredPaymentMethods.find((m) => m.value === value);
      return opt ? opt.text : value;
    },
    invoiceCellClass(sale) {
      if (!sale) return "";
      if (sale.transaction_type === "REFUND") return "is-refund";
      if (sale.is_layaway) return "is-layaway";
      return "";
    },
    statusChipClass(sale) {
      const s = this.getStatusDisplay(sale);
      if (s === "COMPLETED") return "is-teal";
      if (s === "REFUNDED") return "is-danger";
      if (s === "ARCHIVED") return "is-warn";
      if (s === "NEW") return "is-teal";
      if (s === "PROCESSED") return "is-teal";
      if (s === "PARTIAL" || s === "BACKORDER") return "is-warn";
      return "";
    },
    orderTypeChipClass(type) {
      if (type === "home_delivery") return "is-teal";
      if (type === "store_pickup") return "is-warn";
      return "";
    },
    orderTypeIcon(type) {
      const map = {
        home_delivery: "house",
        store_pickup: "shop",
        curbside: "car-front",
        delivery: "truck",
        pickup: "bag",
      };
      return map[type] || "box";
    },
    shortOrderType(type) {
      const map = {
        home_delivery: "Delivery",
        store_pickup: "Pickup",
        curbside: "Curbside",
        delivery: "Delivery",
        pickup: "Pickup",
      };
      return map[type] || this.formatOrderType(type);
    },

    /* ===========================
       ORDERS / DATES
    =========================== */
    formatOrderTypeDisplay(orderType) {
      const typeName = this.formatOrderType(orderType.type);
      return `${typeName} (${orderType.count} item${orderType.count > 1 ? 's' : ''})`;
    },
    isArchived(sale) {
      return sale.status === 10;
    },
    archive_invoice(invoice_id) {
      let that = this;
      let s = function (response) {
        if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        else {
          that.$bvToast.toast("Invoice archived", { title: "Success", autoHideDelay: 1500, variant: "success" });
          that.fetch_init();
        }
      };
      let f = function () {
        that.$bvToast.toast("Error archiving invoice.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
      };
      Requestor.postSyncP("/api/sales/archive_invoice/", { invoice_id }, this, s, f);
    },
    unarchive_invoice(invoice_id) {
      let that = this;
      let s = function (response) {
        if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        else {
          that.$bvToast.toast("Invoice unarchived", { title: "Success", autoHideDelay: 1500, variant: "success" });
          that.fetch_init();
        }
      };
      let f = function () {
        that.$bvToast.toast("Error unarchiving invoice.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
      };
      Requestor.postSyncP("/api/sales/unarchive_invoice/", { invoice_id }, this, s, f);
    },
    getUniqueOrderTypes(sale) {
      if (!sale.items || !Array.isArray(sale.items)) return [];
      const orderTypes = [...new Set(sale.items.map(item => item.order_type))];
      return orderTypes.filter(type => type);
    },
    getOrderTypesWithCount(sale) {
      if (!sale.items || !Array.isArray(sale.items)) return [];
      const typeCount = {};
      sale.items.forEach(item => {
        const type = item.order_type;
        if (type) typeCount[type] = (typeCount[type] || 0) + 1;
      });
      return Object.keys(typeCount).map(type => ({ type, count: typeCount[type] }));
    },
    showOrderTypes(sale) {
      this.selectedSale = { ...sale };
      this.$bvModal.show('order-types-modal');
    },
    formatOrderType(orderType) {
      if (!orderType) return 'Unknown';
      const typeMap = {
        'home_delivery': 'Home Delivery',
        'store_pickup': 'Store Pickup',
        'curbside': 'Curbside Pickup',
        'delivery': 'Delivery',
        'pickup': 'Pickup',
      };
      return typeMap[orderType] || orderType.replace(/_/g, ' ').toUpperCase();
    },
    getOrderTypeItemClass(orderType) {
      const classMap = {
        'home_delivery': 'is-home-delivery',
        'store_pickup': 'is-store-pickup',
        'curbside': 'is-curbside',
        'delivery': 'is-delivery',
        'pickup': 'is-pickup',
      };
      return classMap[orderType] || '';
    },
    getOrderTypeAbbreviation(orderType) {
      const abbrevMap = {
        'home_delivery': 'HD',
        'store_pickup': 'SP',
        'curbside': 'CRB',
        'delivery': 'DEL',
        'pickup': 'PUP',
      };
      return abbrevMap[orderType] || 'OT';
    },
    showDeliveryDates(sale) {
      this.selectedSale = { ...sale };
      this.$bvModal.show('delivery-dates-modal');
    },
    formatDeliveryDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return moment(dateString).format('ddd, MMM DD, YYYY');
      } catch (e) { return dateString; }
    },
    isUpcomingDate(dateString) {
      if (!dateString) return false;
      try {
        const d = moment(dateString).startOf('day');
        const t = moment().startOf('day');
        return d.isSameOrAfter(t);
      } catch (e) { return false; }
    },
    getDateStatus(dateString) {
      if (!dateString) return 'Unknown';
      try {
        const d = moment(dateString).startOf('day');
        const t = moment().startOf('day');
        if (d.isBefore(t)) return 'Completed';
        if (d.isSame(t)) return 'Today';
        if (d.isSame(t.clone().add(1, 'day'))) return 'Tomorrow';
        return `In ${d.diff(t, 'days')} days`;
      } catch (e) { return 'Unknown'; }
    },
    getDateStatusClass(dateString) {
      if (!dateString) return 'is-muted';
      try {
        const d = moment(dateString).startOf('day');
        const t = moment().startOf('day');
        if (d.isBefore(t)) return 'is-ok';
        if (d.isSame(t)) return 'is-warn';
        return 'is-teal';
      } catch (e) { return 'is-muted'; }
    },
    getDateBadgeVariant(index) {
      const variants = ['primary', 'success', 'info', 'warning', 'danger', 'dark'];
      return variants[index % variants.length];
    },
    getUpcomingDatesCount(dates) {
      if (!dates || !Array.isArray(dates)) return 0;
      return dates.filter(date => this.isUpcomingDate(date)).length;
    },

    /* ===========================
       STATUS
    =========================== */
    getStatusDisplay(sale) {
      if (sale.status === 10) return 'ARCHIVED';
      if (sale.transaction_type === 'REFUND') return 'REFUNDED';
      if (sale.status_display) return sale.status_display;
      if (sale.status === 0) return 'NEW';
      if (sale.status === 1) return 'PROCESSED';
      if (sale.completed) return 'COMPLETED';
      if (sale.delivered_items && sale.total_items) {
        if (sale.delivered_items === sale.total_items) return 'COMPLETED';
        if (sale.delivered_items > 0 && sale.delivered_items < sale.total_items) return 'PARTIAL';
      }
      if (sale.backorder_items && sale.backorder_items > 0) return 'BACKORDER';
      return 'OPEN';
    },

    /* ===========================
       NUMBERS / MONEY
    =========================== */
    safeToNumber(value, defaultValue = 0) {
      if (value === null || value === undefined) return defaultValue;
      const num = parseFloat(value);
      return isNaN(num) ? defaultValue : num;
    },
    formatCurrency(value) {
      return this.safeToNumber(value).toFixed(2);
    },
    getTotalPaid(invoice) {
      if (invoice.transaction_type === 'REFUND') {
        return -this.safeToNumber(invoice.display_amount || Math.abs(invoice.transaction_amount) || 0);
      }
      if (!invoice.payment_details || !Array.isArray(invoice.payment_details)) {
        return this.safeToNumber(invoice.paid_so_far);
      }
      return invoice.payment_details.reduce((total, payment) => total + this.safeToNumber(payment.amount), 0);
    },

    /* ===========================
       PAYMENT METHOD / DISPLAY
    =========================== */
    debugPaymentMethods() {
      console.log("=== PAYMENT METHODS DEBUG ===");
      console.log("Selected payment method:", this.picked_payment_method);
      this.sales.forEach((sale, index) => {
        console.log("Sale " + index + " (" + sale.invoice_id + "):", {
          payment_methods: sale.payment_methods || [],
          payment_amount: sale.payment_amount,
          total_payments: sale.total_payments,
          payment_index: sale.payment_index,
        });
      });
    },
    onPaymentMethodChange() { this.debugPaymentMethods(); },
    isPaymentMethodMatch(sale) {
      if (!this.picked_payment_method) return false;
      var pms = sale.payment_methods || [];
      var picked_payment_method = this.picked_payment_method;
      return pms.some(function (payment) {
        if (!payment || !payment.value) return false;
        if (picked_payment_method === "CASH") return payment.value === "cash";
        if (picked_payment_method === "CC") return payment.value === "cc";
        if (picked_payment_method === "STORE_CREDIT") return payment.value === "store_credit";
        if (picked_payment_method === "FINANCE") return payment.value === "finance";
        if (picked_payment_method === "TAX_EXEMPT") return payment.value === "tax_exempt";
        if (picked_payment_method.startsWith("FINANCE - ")) {
          return payment.value === "finance" && payment.name === picked_payment_method.replace("FINANCE - ", "");
        }
        if (picked_payment_method.startsWith("TAX_EXEMPT - ")) {
          return payment.value === "tax_exempt" && payment.name === picked_payment_method.replace("TAX_EXEMPT - ", "");
        }
        return false;
      });
    },
    getPaymentMethodDisplay(method) {
      const map = {
        'cash': 'Cash',
        'cc': 'Credit card',
        'store_credit': 'Store credit',
        'finance': 'Finance',
        'tax_exempt': 'Tax exempt',
      };
      return map[method] || (method ? method.toUpperCase() : 'Unknown');
    },
    getPaymentMethodChipClass(method) {
      if (!method) return "";
      const m = String(method).toUpperCase();
      if (m.startsWith("CASH")) return "is-teal";
      if (m.startsWith("CC") || m.startsWith("CREDIT CARD")) return "is-cc";
      if (m.startsWith("STORE CREDIT")) return "is-warn";
      if (m.startsWith("FINANCE")) return "is-finance";
      if (m.startsWith("TAX EXEMPT")) return "is-danger";
      return "";
    },
    showPaymentHistory(sale) {
      this.selectedInvoice = { ...sale };
      const paymentMethodsToUse = sale.all_payment_methods || sale.payment_methods;
      if (paymentMethodsToUse && Array.isArray(paymentMethodsToUse) && paymentMethodsToUse.length > 0) {
        this.selectedInvoice.payment_details = paymentMethodsToUse.map(payment => ({
          ts: payment.ts,
          value: payment.value,
          amount: payment.amount,
          name: payment.name,
          store_credit_invoice_id: payment.store_credit_invoice_id,
          charged_tax_itself: payment.charged_tax_itself,
          lease_id: payment.lease_id,
          misc_info: payment.misc_info,
        }));
      } else {
        this.selectedInvoice.payment_details = [];
      }
      this.$bvModal.show('payment-history-modal');
    },
    formatPaymentDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return moment(dateString).format('MMM DD, YYYY hh:mm A');
      } catch (e) { return dateString; }
    },
    getPaymentMethodsDisplay(paymentMethods) {
      if (!paymentMethods || paymentMethods.length === 0) return [];
      if (paymentMethods.length > 0 && typeof paymentMethods[0] === 'object') {
        const methodMap = {
          'cash': 'Cash',
          'cc': 'CC',
          'store_credit': 'Store credit',
          'finance': 'Finance',
          'tax_exempt': 'Tax exempt',
        };
        return [...new Set(
          paymentMethods
            .filter(m => m && m.value)
            .map(m => {
              const base = methodMap[m.value] || m.value.toUpperCase();
              if (m.value === 'finance' && m.name && m.name !== 'N/A') return `${base} · ${m.name}`;
              if (m.value === 'tax_exempt' && m.name && m.name !== 'N/A') return `${base} · ${m.name}`;
              return base;
            })
        )];
      }
      return [];
    },
    getPaymentMethodIcon(method) {
      const iconMap = {
        'cash': '💵',
        'cc': '💳',
        'store_credit': '📋',
        'finance': '🏦',
        'tax_exempt': '🧾',
      };
      return iconMap[method] || '💰';
    },
    isRecentPayment(dateString) {
      if (!dateString) return false;
      try {
        return moment().diff(moment(dateString), 'days') <= 7;
      } catch (e) { return false; }
    },
    navigateToSelectedInvoice() {
      if (this.selectedInvoice) {
        this.$bvModal.hide('payment-history-modal');
        this.$router.push("/sales/viewsale/" + this.selectedInvoice.invoice_id + "/");
      }
    },

    /* ===========================
       LOCATIONS / CHUNKS
    =========================== */
    getFilteredLocations() {
      const excludedLocations = ["101 WH", "FOLCROFT WH", "9TH ST WH"];
      if (!this.packet.locations) return [];
      return this.packet.locations.filter(location => {
        if (this.packet.userDetails.properties.location_id) {
          return location.value == this.packet.userDetails.properties.location_id;
        }
        return !excludedLocations.includes(location.text.toUpperCase());
      });
    },
    row_part_of_chunk(idx) {
      let status = false;
      if (this.chunk_allowed) {
        let chunk = this.chunkoptions.filter(d => d.value == this.chunk_allowed);
        if (chunk.length > 0) {
          chunk = chunk[0];
          if (idx >= chunk.start && idx <= chunk.end) status = true;
        }
      }
      return status;
    },
    chunk_n(items) {
      this.chunkoptions = [];
      let n = items.length;
      if (n == 0) {
        this.chunk_allowed = 0;
        return;
      }
      let each = this.chunksize;
      if (n <= this.chunksize) each = n;
      for (var i = 0; i < n; i = i + each) {
        this.chunkoptions.push({
          start: i,
          end: i + each,
          value: i + each,
          text: i + " TO " + (i + each),
        });
      }
      this.chunk_allowed = this.chunkoptions[0].value;
    },

    /* ===========================
       TOTALS / FILTERS
    =========================== */
    total() {
      let t = 0.0;
      for (var i = 0; i < this.sales.length; i++) {
        let sale = this.sales[i];
        if (this.allowed(sale)) {
          if (sale.transaction_type === 'REFUND') {
            t -= this.safeToNumber(sale.display_amount || Math.abs(sale.transaction_amount) || 0);
          } else {
            t += this.safeToNumber(sale.payment_amount || sale.paid_so_far || 0);
          }
        }
      }
      return t;
    },
    allowed(row) {
      let status = true;
      const isArchived = this.isArchived(row);
      if (this.combined_status_filter !== "archived" && isArchived) return false;
      if (this.sale_type_filter === "regular") {
        if (row.is_layaway || row.transaction_type === "REFUND") status = false;
      } else if (this.sale_type_filter === "layaway") {
        if (!row.is_layaway || row.transaction_type === "REFUND") status = false;
      } else if (this.sale_type_filter === "refund") {
        if (row.transaction_type !== "REFUND") status = false;
      }
      if (!status) return false;
      if (this.orderTypeFilter !== "all") {
        const hasMatchingOrderType = row.items && row.items.some(item => item.order_type === this.orderTypeFilter);
        if (!hasMatchingOrderType) status = false;
      }
      if (!status) return false;
      if (this.combined_status_filter !== "all") {
        const currentStatus = this.getStatusDisplay(row);
        switch (this.combined_status_filter) {
          case "new": if (currentStatus !== "NEW") status = false; break;
          case "open": if (currentStatus !== "OPEN") status = false; break;
          case "processed": if (currentStatus !== "PROCESSED") status = false; break;
          case "backorder": if (currentStatus !== "BACKORDER") status = false; break;
          case "partial": if (currentStatus !== "PARTIAL") status = false; break;
          case "delivered": if (currentStatus !== "DELIVERED") status = false; break;
          case "completed": if (currentStatus !== "COMPLETED") status = false; break;
          case "archived": if (!isArchived) status = false; break;
        }
      }
      if (!status) return false;
      if (this.picked_location_id != null && row.location_id != this.picked_location_id) status = false;
      if (!status) return false;
      if (this.picked_payment_method != null) {
        var pms = row.payment_methods || [];
        var picked = this.picked_payment_method;
        var hasMatch = pms.some(function (payment) {
          if (!payment || !payment.value) return false;
          if (picked === "CASH") return payment.value === "cash";
          if (picked === "CC") return payment.value === "cc";
          if (picked === "STORE_CREDIT") return payment.value === "store_credit";
          if (picked === "FINANCE") return payment.value === "finance";
          if (picked === "TAX_EXEMPT") return payment.value === "tax_exempt";
          if (picked.startsWith("FINANCE - ")) return payment.value === "finance" && payment.name === picked.replace("FINANCE - ", "");
          if (picked.startsWith("TAX_EXEMPT - ")) return payment.value === "tax_exempt" && payment.name === picked.replace("TAX_EXEMPT - ", "");
          return false;
        });
        if (!hasMatch) status = false;
      }
      return status;
    },

    /* ===========================
       SEARCH
    =========================== */
    search_dates(e) {
      if (e) e.preventDefault();
      this.query.invoice_id = null;
      this.fetch_init();
    },
    search_invoice_id(e) {
      if (e) e.preventDefault();
      if (!this.query.invoice_id) {
        this.$bvToast.toast("Please specify a valid invoice id", { title: "Error", autoHideDelay: 1500, variant: "warning" });
        return;
      }
      this.fetch_init();
    },

    /* ===========================
       VOID / FETCH
    =========================== */
    void_invoice(invoice_id) {
      let that = this;
      let s = function (response) {
        if (response.data.error) that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        else {
          that.$bvToast.toast("Invoice voided", { title: "Success", autoHideDelay: 1500, variant: "success" });
          that.sales = that.sales.filter(d => d.invoice_id != invoice_id);
        }
      };
      let f = function () {
        that.$bvToast.toast("Error voiding invoice.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
      };
      Requestor.postSyncP("/api/sales/void_invoice/", { invoice_id }, this, s, f);
    },
    fetch_prereqs() {
      let that = this;
      Requestor.getAsyncP(
        "/api/finance_company/get_all_finance_companies/",
        {},
        this,
        function (response) {
          if (response.data.result) that.finance_companies = response.data.result;
          else that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        },
        function () {
          that.$bvToast.toast("Error loading finance companies list.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
        }
      );
    },
    fetch_init() {
      let that = this;
      let path = "/api/sales/get_sales_for_other_invoices/";
      let query = JSON.parse(JSON.stringify(this.query));
      if (query.invoice_id) {
        query.invoice_id = query.invoice_id.replace(/^\s+|\s+$/gm, '');
        query.startDate = null;
        query.endDate = null;
      }
      let data = {
        userid: this.packet.userDetails.id,
        query: query,
        status: this.status_filter !== "all" ? this.status_filter : null,
      };
      if (this.packet.userDetails.properties.location_id) {
        data.location_id = this.packet.userDetails.properties.location_id;
      }
      let s = function (response) {
        if (response.data.error) {
          that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
        } else {
          that.sales = response.data.result;
          if (that.sales) {
            that.sales = that.sales.map(function (d) {
              d.payment_methods = ((d.payment_methods == null) ? [] : d.payment_methods);
              d.completed = !!d.completed;
              d.is_layaway = !!d.is_layaway;
              d.total_payments = d.total_payments || 1;
              d.payment_index = d.payment_index || 0;
              return d;
            });
            that.sales.sort((a, b) => {
              if (a.completed !== b.completed) return a.completed ? 1 : -1;
              if (a.is_layaway !== b.is_layaway) return a.is_layaway ? -1 : 1;
              return 0;
            });
          }
          that.chunk_n(that.sales);
        }
        that.sales_being_loaded = false;
      };
      let f = function () {
        that.$bvToast.toast("Error loading sales.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
        that.sales_being_loaded = false;
      };
      this.sales_being_loaded = true;
      Requestor.postSyncP(path, data, this, s, f);
    },
    navigateToInvoice(idx) {
      let sale = this.sales[idx];
      let invoice_id = sale.invoice_id;
      const routeData = this.$router.resolve({ path: `/sales/viewsale/${invoice_id}/` });
      window.open(routeData.href, "_blank", "noopener,noreferrer");
    },
    sortby(key) {
      if (key === 'completed') {
        this.sales.sort((a, b) => {
          if (a.completed === b.completed) return 0;
          if (this.sort_asc) return !a.completed && b.completed ? -1 : 1;
          return a.completed && !b.completed ? -1 : 1;
        });
      } else if (key === 'status_display') {
        this.sales.sort((a, b) => {
          const sA = this.getStatusDisplay(a);
          const sB = this.getStatusDisplay(b);
          return this.sort_asc ? sA.localeCompare(sB) : sB.localeCompare(sA);
        });
      } else {
        if (this.sort_asc) this.sales = Requestor.sort(this.sales, key);
        else this.sales = Requestor.sort_reverse(this.sales, key);
      }
      this.sort_asc = !this.sort_asc;
    },

    /* ===========================
       CSV
    =========================== */
    get_csv_data() {
      let data = [];
      let items = this.sales;
      if (items && items.length > 0) {
        for (var i = 0; i < items.length; i++) {
          let row = items[i];
          if (!this.allowed(row)) continue;
          if (!row.items || !Array.isArray(row.items)) continue;
          for (var j = 0; j < row.items.length; j++) {
            let element = row.items[j];
            if (!element) continue;
            if (element.order_type == 'home_delivery' || element.order_type == 'store_pickup') {
              let k = {};
              k["Bill To Name"] = row.customer_fullname || '';
              k["Email Address"] = row.email || '';
              k["Ship-to Name"] = row.customer_fullname || '';
              k["Ship-to Phone"] = (row.customer_phone || '').replace(/\(/g, "").replace(/\)/g, "");
              k["Ship-to Address Line 1"] = row.address || '';
              k["Ship-to Address Line 2"] = "";
              k["Ship-to City"] = row.city || '';
              k["Stst"] = row.state || '';
              k["St Postal"] = row.zipcode || '';
              k["Order No"] = row.invoice_id || '';
              k["Store"] = this.packet.locsubsdict[row.location_id + "|" + row.sublocation_id] || '';
              k["Tr Cd"] = 0;
              let paymentAmount = 0;
              if (row.is_layaway) paymentAmount = this.safeToNumber(row.paid_so_far);
              else paymentAmount = this.safeToNumber(row.payment_amount || row.grand_total);
              k["Order Total"] = this.formatCurrency(paymentAmount);
              k["Product Description"] = element.product_name || '';
              k["Stk Lc"] = this.packet.locsubsdict[element.locsub] || '';
              k["Product Number"] = element.product_sku || '';
              k["Qty Ord"] = element.quantity || 0;
              k["Qty Rsvd"] = element.quantity || 0;
              k["Case Price"] = 0.0;
              k["Volume"] = element.cubes || 0;
              k["Del Dates"] = element.delivery_date ? moment(element.delivery_date).format("MM/DD/YYYY") : '';
              k["Delivery Type"] = element.order_type || '';
              k["Sale Type"] = row.is_layaway ? "LAYAWAY" : "REGULAR";
              k["Status"] = this.getStatusDisplay(row);
              k["Completed"] = row.completed ? "YES" : "NO";
              let paymentMethod = 'N/A';
              if (row.payment_methods && row.payment_methods.length > 0 && row.payment_methods[0]) {
                paymentMethod = this.getPaymentMethodDisplay(row.payment_methods[0].value);
              }
              k["Payment Method"] = paymentMethod;
              k["Payment Amount"] = this.formatCurrency(paymentAmount);
              k["Payment Date"] = row.datetime || '';
              data.push(k);
            }
          }
        }
      }
      return data;
    },
    get_csv_filename() {
      let now = moment().format("YYYY-MM-DD_HHmmss");
      return `SALES_WITH_PAYMENTS_${now}.csv`;
    },
  },
  created: function () {
    this.packet = JSON.parse(localStorage.packet);
    this.packet.userDetails = JSON.parse(localStorage.userDetails);
    if (this.packet.userDetails) this.fetch_prereqs();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  mounted: function () {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
    const Lightpick = require('lightpick');
    let that = this;
    new Lightpick({
      field: document.getElementById('dates'),
      singleDate: false,
      repick: true,
      onSelect: function (start, end) {
        that.query.startDate = start ? start.startOf('day').format('YYYY-MM-DD') : '';
        that.query.endDate = end ? end.endOf('day').format('YYYY-MM-DD') : '';
      }
    });
    this.fetch_init();
  },
}
</script>

<style scoped>
/* ===================================================== */
/* PAGE SHELL                                            */
/* ===================================================== */
.ls-page {
  background: #F6F4F0;
  min-height: 100vh;
  padding: 18px 0 60px;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #221F1C;
  font-size: 17px;
}
.ls-wrap { max-width: 1800px; width: 97%; margin: 0 auto; }
@media (max-width: 767px) {
  .ls-page { padding: 10px 0 40px; font-size: 16px; }
  .ls-wrap { width: 100%; padding: 0 12px; }
}

/* ===================================================== */
/* LOADING OVERLAY                                       */
/* ===================================================== */
.ls-loading-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(34, 31, 28, 0.82);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
}
.ls-loading-card {
  background: #fff;
  padding: 36px 50px;
  border-radius: 20px;
  text-align: center;
  border-bottom: 5px solid #EE7A22;
  min-width: 360px;
}
.ls-logo {
  width: 60px; height: 60px; border-radius: 14px;
  background: #EE7A22; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-family: 'Sora', sans-serif; font-weight: 800; font-size: 24px;
}
.ls-logo--lg { width: 72px; height: 72px; font-size: 28px; margin-bottom: 18px; animation: lsPulse 1.2s ease-in-out infinite; }
@keyframes lsPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(0.92); } }
.ls-loading-text { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; margin-bottom: 18px; }
.ls-loading-bar { width: 300px; height: 8px; background: #F1EEE9; border-radius: 4px; overflow: hidden; margin: 0 auto; }
.ls-loading-fill { height: 100%; background: #EE7A22; border-radius: 4px; }

.ls-fade-enter-active, .ls-fade-leave-active { transition: opacity 0.25s ease; }
.ls-fade-enter, .ls-fade-leave-to { opacity: 0; }
.ls-fade-up-enter-active { animation: lsSlideUp 0.4s ease-out; }
@keyframes lsSlideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
.ls-pop-enter-active { animation: lsPopIn 0.28s cubic-bezier(0.34, 1.2, 0.64, 1); }
@keyframes lsPopIn { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }

/* ===================================================== */
/* HERO                                                  */
/* ===================================================== */
.ls-hero {
  background: #221F1C;
  color: #fff;
  border-radius: 20px;
  overflow: hidden;
  border-bottom: 5px solid #EE7A22;
  margin-bottom: 14px;
}
.ls-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
  gap: 24px;
  padding: 22px 30px;
  align-items: center;
}
.ls-hero-label {
  font-size: 15px; color: rgba(255, 255, 255, 0.6);
  font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em;
}
.ls-hero-title {
  font-family: 'Sora', sans-serif;
  font-size: 42px; font-weight: 800;
  line-height: 1.1;
  margin: 4px 0 12px;
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
}
.ls-count-pill {
  display: inline-flex; align-items: center; justify-content: center;
  background: #EE7A22; color: #fff;
  font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 800;
  padding: 5px 16px; border-radius: 999px;
}
.ls-hero-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.ls-tag {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 15px; font-weight: 700;
  padding: 6px 15px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.14);
}
.ls-tag.is-orange { background: rgba(238, 122, 34, 0.22); color: #FFB06B; border-color: rgba(238, 122, 34, 0.45); }
.ls-tag.is-teal   { background: rgba(15, 110, 107, 0.24); color: #7FDACF; border-color: rgba(15, 110, 107, 0.5); }
.ls-hero-qr {
  display: flex; align-items: center; gap: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px; padding: 14px 20px;
  color: #FFB06B;
}
.ls-qr-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 17px; color: #fff; }
.ls-qr-sub { font-size: 15px; color: rgba(255, 255, 255, 0.65); }
.ls-hero-actions {
  display: flex; gap: 10px; flex-wrap: wrap;
  padding: 12px 30px; background: #34302B;
}
.ls-hero-actions .btn { padding: 9px 18px !important; font-size: 16px !important; }
@media (max-width: 900px) {
  .ls-hero-grid { grid-template-columns: 1fr; gap: 14px; }
  .ls-hero-title { font-size: 32px; }
  .ls-hero-actions { padding: 10px 18px; }
}

/* ===================================================== */
/* BUTTONS                                               */
/* ===================================================== */
.ls-btn-primary.btn,
.ls-btn-ghost.btn,
.ls-btn-ghost-dark.btn {
  border-radius: 10px !important;
  font-weight: 700; font-size: 16.5px;
  padding: 10px 18px;
  display: inline-flex; align-items: center; gap: 8px;
  box-shadow: none !important;
}
.ls-btn-primary.btn {
  background: #EE7A22 !important;
  border: 1.5px solid #EE7A22 !important;
  color: #fff !important;
}
.ls-btn-primary.btn:hover:not(:disabled) {
  background: #D4620F !important;
  border-color: #D4620F !important;
}
.ls-btn-ghost.btn {
  background: #fff !important;
  color: #221F1C !important;
  border: 1.5px solid #E5E1DA !important;
}
.ls-btn-ghost.btn:hover:not(:disabled) { border-color: #EE7A22 !important; color: #D4620F !important; }
.ls-btn-ghost-dark.btn {
  background: rgba(255, 255, 255, 0.06) !important;
  color: #fff !important;
  border: 1.5px solid rgba(255, 255, 255, 0.22) !important;
}
.ls-btn-ghost-dark.btn:hover:not(:disabled) { border-color: #EE7A22 !important; }
.ls-download-btn { text-decoration: none; }
.ls-page .btn:disabled { opacity: 0.5; }

/* ===================================================== */
/* CARDS                                                 */
/* ===================================================== */
.ls-card {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 14px;
}

/* ===================================================== */
/* SINGLE-ROW FILTER STRIP                               */
/* ===================================================== */
.ls-filterstrip { padding: 12px 14px !important; }
.ls-filters-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 3px;
  scrollbar-width: thin;
}
.ls-filters-bar::-webkit-scrollbar { height: 8px; }
.ls-filters-bar::-webkit-scrollbar-thumb { background: #DCD6CC; border-radius: 8px; }

.ls-fb-item { flex: 0 0 auto; min-width: 0; }
.ls-fb-search { flex: 1 1 280px; min-width: 240px; }
.ls-fb-dates  { flex: 0 1 260px; min-width: 220px; }

.ls-fb-inputgroup { flex-wrap: nowrap; width: 100%; }
.ls-fb-inputgroup >>> .input-group-text {
  height: 48px;
  padding: 0 14px;
  background: #F1EEE9;
  border: 1.5px solid #E5E1DA;
  border-right: 0;
  border-radius: 10px 0 0 10px !important;
  color: #736C63;
  font-size: 17px;
}
.ls-fb-inputgroup >>> .form-control {
  height: 48px !important;
  font-size: 17px !important;
  font-weight: 600 !important;
  border: 1.5px solid #E5E1DA !important;
  background: #F7F5F2 !important;
  color: #221F1C !important;
  border-radius: 0 !important;
  padding: 0 14px !important;
}
.ls-fb-inputgroup >>> .form-control:focus {
  border-color: #EE7A22 !important;
  background: #fff !important;
  box-shadow: none !important;
}
.ls-fb-inputgroup >>> .input-group-append .btn {
  height: 48px;
  border-radius: 0 10px 10px 0 !important;
  padding: 0 18px !important;
  font-size: 15px !important;
}

.ls-fb-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #F7F5F2;
  border: 1.5px solid #E5E1DA;
  border-radius: 999px;
  padding: 0 16px 0 14px;
  height: 48px;
  flex-shrink: 0;
}
.ls-fb-pill-icon { color: #EE7A22; font-size: 17px; flex-shrink: 0; }
.ls-fb-select {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  font-size: 16.5px !important;
  font-weight: 700 !important;
  color: #221F1C !important;
  padding: 0 26px 0 4px !important;
  height: 46px !important;
  min-width: 160px;
  background-position: right 6px center !important;
  background-size: 14px !important;
  cursor: pointer;
}
.ls-fb-select:focus { box-shadow: none !important; outline: none !important; }
.ls-fb-btn { padding: 0 18px !important; font-size: 15px !important; }
.ls-fb-reset.btn {
  height: 48px;
  width: 48px;
  padding: 0 !important;
  border-radius: 999px !important;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 17px;
}

@media (max-width: 900px) {
  .ls-filters-bar { flex-wrap: wrap; overflow-x: visible; }
  .ls-fb-search, .ls-fb-dates { flex: 1 1 100%; min-width: 0; }
  .ls-fb-pill { flex: 1 1 calc(50% - 5px); }
  .ls-fb-select { min-width: 0; width: 100%; }
}

/* ===================================================== */
/* STATS                                                 */
/* ===================================================== */
.ls-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.ls-stat {
  display: flex; align-items: center; gap: 14px;
  background: #fff;
  border: 1px solid #E5E1DA;
  border-left: 6px solid transparent;
  border-radius: 14px;
  padding: 14px 16px;
}
.ls-stat-icon {
  width: 50px; height: 50px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 22px; flex-shrink: 0;
}
.ls-stat-body { display: flex; flex-direction: column; min-width: 0; }
.ls-stat-value {
  font-family: 'Sora', sans-serif;
  font-size: 26px; font-weight: 800;
  line-height: 1.1; letter-spacing: -0.01em;
}
.ls-stat-label { font-size: 14.5px; color: #736C63; font-weight: 600; margin-top: 2px; }
.ls-stat.is-total   { border-left-color: #D4620F; }
.ls-stat.is-total   .ls-stat-icon { background: linear-gradient(135deg, #EE7A22, #D4620F); }
.ls-stat.is-total   .ls-stat-value { color: #D4620F; }
.ls-stat.is-count   { border-left-color: #0F6E6B; }
.ls-stat.is-count   .ls-stat-icon { background: linear-gradient(135deg, #0F6E6B, #0B5552); }
.ls-stat.is-count   .ls-stat-value { color: #0F6E6B; }
.ls-stat.is-refund  { border-left-color: #C93C30; }
.ls-stat.is-refund  .ls-stat-icon { background: linear-gradient(135deg, #C93C30, #A22A1F); }
.ls-stat.is-refund  .ls-stat-value { color: #C93C30; }
.ls-stat.is-layaway { border-left-color: #C98A12; }
.ls-stat.is-layaway .ls-stat-icon { background: linear-gradient(135deg, #E0A524, #C98A12); }
.ls-stat.is-layaway .ls-stat-value { color: #C98A12; }
@media (max-width: 991px) { .ls-stats { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .ls-stats { grid-template-columns: 1fr; } }

/* ===================================================== */
/* TABLE                                                 */
/* ===================================================== */
.ls-table-card { padding: 0 !important; overflow: hidden; }
.ls-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.ls-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 17px;
  min-width: 1360px;
}
.ls-th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #221F1C;
  color: #fff;
  text-align: left;
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 18px 14px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  border-bottom: 3px solid #EE7A22;
}
.ls-th:hover { background: #34302B; }
.ls-th--actions { cursor: default; }
.ls-th--actions:hover { background: #221F1C; }
.ls-sort-icon { font-size: 13px; opacity: 0.6; margin-left: 5px; }

.ls-totals-row { background: #FFF1E4; font-weight: 800; }
.ls-totals-row td { padding: 14px; border-bottom: 2px solid #F5CFAE; }
.ls-totals-label {
  font-family: 'Sora', sans-serif;
  font-size: 15px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #D4620F;
}
.ls-totals-amt {
  display: flex; align-items: center; gap: 8px;
  color: #D4620F;
  font-family: 'Sora', sans-serif;
  font-size: 20px;
  font-weight: 800;
}

.ls-row {
  animation: lsRowIn 0.35s ease-out backwards;
  transition: background 0.15s ease;
}
@keyframes lsRowIn {
  from { opacity: 0; transform: translateX(-4px); }
  to { opacity: 1; transform: translateX(0); }
}
.ls-row td {
  padding: 16px 14px;
  border-bottom: 1px solid #F1EEE9;
  vertical-align: top;
  background: #fff;
}
.ls-row:hover td { background: #FAF9F7; }
.ls-row.is-refund td { background: #FFF6F4; }
.ls-row.is-refund:hover td { background: #FEEFEC; }
.ls-row.is-layaway td { background: #FFFCF3; }
.ls-row.is-layaway:hover td { background: #FFF8E6; }
.ls-row.is-highlight td {
  background: #EAF6F4;
  box-shadow: inset 0 0 0 1px rgba(15, 110, 107, 0.25);
}

.ls-td { color: #221F1C; }
.ls-td--actions { vertical-align: middle; }

.ls-invoice-cell {
  display: flex; flex-direction: column; gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #F7F5F2;
  border-left: 4px solid #EE7A22;
}
.ls-invoice-cell.is-refund   { background: #FBE9E7; border-left-color: #C93C30; }
.ls-invoice-cell.is-layaway  { background: #FFF4DC; border-left-color: #C98A12; }
.ls-invoice-id {
  font-family: 'Sora', sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: #221F1C;
  word-break: break-all;
}
.ls-invoice-date {
  font-family: 'Menlo', monospace;
  font-size: 14px;
  font-weight: 700;
  color: #736C63;
  white-space: nowrap;
}
.ls-invoice-tags { display: flex; flex-wrap: wrap; gap: 5px; }

.ls-mini-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.03em;
  padding: 4px 10px;
  border-radius: 999px;
  background: #F1EEE9;
  color: #4A4540;
  border: 1px solid #E5E1DA;
  white-space: nowrap;
}
.ls-mini-chip.is-teal   { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.ls-mini-chip.is-warn   { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.ls-mini-chip.is-danger { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }
.ls-mini-chip.is-cc     { background: #E8EFF7; color: #234A66; border-color: #BBD5E8; }
.ls-mini-chip.is-finance{ background: #EEE6F9; color: #5E37A3; border-color: #D4C0F0; }

.ls-cust-cell { display: flex; flex-direction: column; gap: 4px; }
.ls-cust-name { font-weight: 800; font-size: 16px; color: #221F1C; line-height: 1.3; }
.ls-cust-line {
  font-size: 14.5px; color: #4A4540;
  display: flex; align-items: center; gap: 6px;
  max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ls-cust-line .b-icon { color: #736C63; font-size: 14px; }
.ls-cust-line.is-muted { color: #A59D92; }

.ls-loc-cell { display: flex; flex-direction: column; gap: 5px; }
.ls-loc-primary {
  font-weight: 700; font-size: 15px;
  color: #221F1C; line-height: 1.35;
  display: flex; align-items: center; gap: 6px;
}
.ls-loc-primary .b-icon { color: #EE7A22; font-size: 14px; flex-shrink: 0; }
.ls-loc-secondary { font-size: 14px; color: #736C63; padding-left: 20px; }
.ls-agent-chip {
  display: inline-flex; align-items: center; gap: 6px;
  align-self: flex-start; margin-top: 4px;
  font-size: 13.5px; font-weight: 700; letter-spacing: 0.02em;
  padding: 4px 11px; border-radius: 999px;
  background: #F1EEE9; color: #4A4540; border: 1px solid #E5E1DA;
}
.ls-agent-chip .b-icon { color: #736C63; font-size: 13px; }

.ls-od-cell { display: flex; flex-direction: column; gap: 8px; }
.ls-order-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.ls-deliv-line { display: flex; flex-wrap: wrap; gap: 5px; }

.ls-mini-btn.btn {
  background: #fff !important;
  color: #221F1C !important;
  border: 1.5px solid #E5E1DA !important;
  border-radius: 9px !important;
  font-size: 14px !important;
  font-weight: 700;
  padding: 6px 12px !important;
  display: inline-flex; align-items: center; gap: 6px;
  box-shadow: none !important;
  line-height: 1.2 !important;
}
.ls-mini-btn.btn.is-tiny { font-size: 13px !important; padding: 4px 10px !important; }
.ls-mini-btn.btn:hover { border-color: #EE7A22 !important; color: #D4620F !important; }
.ls-mini-btn.btn.is-primary {
  background: #EE7A22 !important;
  border-color: #EE7A22 !important;
  color: #fff !important;
}
.ls-mini-btn.btn.is-primary:hover {
  background: #D4620F !important;
  border-color: #D4620F !important;
  color: #fff !important;
}
.ls-mini-btn.btn.is-teal {
  background: #0F6E6B !important;
  border-color: #0F6E6B !important;
  color: #fff !important;
}
.ls-mini-btn.btn.is-teal:hover { background: #0B5552 !important; border-color: #0B5552 !important; }
.ls-mini-btn.btn.is-danger { color: #C93C30 !important; }
.ls-mini-btn.btn.is-danger:hover {
  background: #C93C30 !important;
  border-color: #C93C30 !important;
  color: #fff !important;
}

.ls-muted-small { font-size: 14px; color: #A59D92; font-style: italic; }

.ls-pay-cell {
  display: flex; flex-direction: column; gap: 5px;
  cursor: pointer; padding: 6px 8px; border-radius: 8px;
  transition: background 0.15s ease;
}
.ls-pay-cell:hover { background: #FFF1E4; }
.ls-pay-amt {
  font-family: 'Sora', sans-serif;
  font-size: 20px; font-weight: 800; line-height: 1.1;
}
.ls-pay-amt.is-teal   { color: #0F6E6B; }
.ls-pay-amt.is-danger { color: #C93C30; }
.ls-methods-cell { display: flex; flex-wrap: wrap; gap: 4px; }
.ls-pay-note { font-size: 13.5px; color: #A59D92; }

.ls-status-badge {
  display: inline-flex; flex-direction: column; gap: 3px;
  font-family: 'Sora', sans-serif;
  font-size: 14.5px; font-weight: 800; letter-spacing: 0.03em;
  padding: 8px 12px; border-radius: 9px;
  background: #F1EEE9; color: #4A4540; border: 1px solid #E5E1DA;
  text-transform: uppercase; min-width: 110px; text-align: center;
}
.ls-status-badge.is-teal   { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.ls-status-badge.is-warn   { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.ls-status-badge.is-danger { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }
.ls-status-sub {
  font-family: 'Manrope', sans-serif;
  font-size: 12px; font-weight: 600;
  letter-spacing: 0; text-transform: none; opacity: 0.75;
}

.ls-actions {
  display: flex; flex-wrap: wrap; gap: 5px; align-items: center;
}

/* ===================================================== */
/* EMPTY / LOADING                                       */
/* ===================================================== */
.ls-empty {
  text-align: center;
  padding: 60px 30px;
  border: 2px dashed #D6CFC4;
  background: #FAF9F7;
}
.ls-empty-icon {
  width: 78px; height: 78px; border-radius: 22px;
  background: #FFF1E4; color: #EE7A22;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 34px; margin-bottom: 18px;
}
.ls-empty h3 { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 700; margin: 0 0 8px; }
.ls-empty p { color: #736C63; max-width: 50ch; margin: 0 auto 20px; line-height: 1.6; font-size: 17px; }

.ls-empty-inline {
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 40px 20px; color: #A59D92;
}

/* ===================================================== */
/* MODAL                                                 */
/* ===================================================== */
.ls-modal.modal-content {
  border: 0; border-radius: 20px; overflow: hidden;
  box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5);
}
.ls-modal .modal-header {
  background: #221F1C !important;
  color: #fff !important;
  border: 0 !important;
  border-bottom: 4px solid #EE7A22 !important;
  padding: 18px 26px !important;
}
.ls-modal .modal-header .close { color: #fff !important; opacity: 0.85; text-shadow: none; }
.ls-modal .modal-body { background: #F6F4F0; padding: 22px 24px; }

.ls-modal-title { display: flex; align-items: center; gap: 16px; }
.ls-modal-title-icon {
  width: 48px; height: 48px; border-radius: 12px;
  background: rgba(238, 122, 34, 0.2);
  color: #FFB06B;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 20px;
}
.ls-modal-title-main { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; }
.ls-modal-title-sub { font-size: 15px; color: rgba(255, 255, 255, 0.65); }

.ls-modal-body { display: flex; flex-direction: column; gap: 16px; font-size: 17px; }
.ls-modal-summary {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; flex-wrap: wrap;
  background: #fff; border: 1px solid #E5E1DA; border-radius: 14px;
  padding: 16px 20px;
}
.ls-modal-summary strong { font-family: 'Sora', sans-serif; font-size: 19px; font-weight: 700; }
.ls-modal-summary small { display: block; color: #736C63; font-size: 15px; margin-top: 3px; }

.ls-modal-foot {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; flex-wrap: wrap;
  padding-top: 16px; border-top: 1px solid #E5E1DA;
}
.ls-foot-note { font-size: 15px; color: #736C63; font-weight: 600; }
.ls-foot-note b { color: #221F1C; }
.ls-modal-foot-actions { display: flex; gap: 10px; flex-wrap: wrap; }

.ls-date-list, .ls-order-list {
  list-style: none; padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: 10px;
  max-height: 400px; overflow-y: auto;
}
.ls-date-item, .ls-order-item {
  display: flex; align-items: center; gap: 16px;
  background: #fff;
  border: 1px solid #E5E1DA;
  border-left: 4px solid #EE7A22;
  border-radius: 12px;
  padding: 14px 18px;
}
.ls-date-item:hover, .ls-order-item:hover { box-shadow: 0 6px 16px -10px rgba(34, 31, 28, 0.25); }
.ls-date-item.is-upcoming { border-left-color: #0F6E6B; }
.ls-date-icon, .ls-order-icon {
  width: 44px; height: 44px; border-radius: 10px;
  background: #FFF1E4; color: #D4620F;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
}
.ls-order-item.is-home-delivery  { border-left-color: #0F6E6B; }
.ls-order-item.is-store-pickup   { border-left-color: #C98A12; }
.ls-order-item.is-curbside       { border-left-color: #EE7A22; }
.ls-order-item.is-delivery       { border-left-color: #234A66; }
.ls-order-item.is-pickup         { border-left-color: #5E37A3; }
.ls-date-details, .ls-order-details { flex: 1 1 auto; min-width: 0; }
.ls-date-display, .ls-order-display { font-weight: 700; font-size: 17px; color: #221F1C; }
.ls-date-status, .ls-order-count { font-size: 15px; font-weight: 600; margin-top: 3px; }
.ls-date-status.is-ok    { color: #0F6E6B; }
.ls-date-status.is-warn  { color: #C98A12; }
.ls-date-status.is-teal  { color: #2B83B5; }
.ls-date-status.is-muted { color: #736C63; }

.ls-pay-modal-head {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 16px; background: #fff;
  border: 1px solid #E5E1DA; border-radius: 14px;
  padding: 18px 20px;
}
.ls-pay-modal-customer strong { font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 700; display: block; }
.ls-pay-modal-customer small { display: block; color: #736C63; font-size: 15px; margin-top: 4px; }
.ls-pay-modal-tags { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 10px; }
.ls-pay-modal-totals { display: flex; flex-direction: column; gap: 6px; }
.ls-pay-tot-row {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 10px; font-size: 16px; padding: 7px 0;
  border-bottom: 1px dashed #E5E1DA;
}
.ls-pay-tot-row:last-child { border-bottom: 0; }
.ls-pay-tot-row span { color: #736C63; font-weight: 600; }
.ls-pay-tot-row strong { font-family: 'Sora', sans-serif; font-size: 21px; font-weight: 800; }
.ls-pay-tot-row.is-teal strong { color: #0F6E6B; }
.ls-pay-tot-row.is-danger strong { color: #C93C30; }

.ls-pay-progress {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 14px;
  padding: 18px 20px;
}
.ls-pay-progress-head {
  display: flex; justify-content: space-between;
  font-size: 15px; font-weight: 600;
  color: #736C63; margin-bottom: 10px;
}
.ls-pay-progress-head strong { color: #221F1C; font-size: 17px; }
.ls-pay-progress-foot {
  display: flex; justify-content: space-between; flex-wrap: wrap; gap: 8px;
  margin-top: 10px; font-size: 14.5px; font-weight: 700;
}
.ls-pay-progress-foot .is-warn   { color: #8A5E09; }
.ls-pay-progress-foot .is-danger { color: #C93C30; }

.ls-pay-section {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 14px;
  overflow: hidden;
}
.ls-pay-section-head {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 18px;
  background: #221F1C;
  color: #fff;
  font-weight: 700; font-size: 17px;
}
.ls-pay-section-head .b-icon { color: #FFB06B; font-size: 18px; }
.ls-pay-section-head .ls-chip {
  background: rgba(255, 255, 255, 0.12);
  color: #fff; border-color: rgba(255, 255, 255, 0.2);
  margin-left: auto;
}
.ls-chip {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 14px; font-weight: 700;
  padding: 5px 13px; border-radius: 999px;
  background: #F1EEE9; color: #4A4540; border: 1px solid #E5E1DA;
  white-space: nowrap;
}
.ls-chip.is-teal   { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.ls-chip.is-warn   { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.ls-chip.is-danger { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }

.ls-pay-list { list-style: none; margin: 0; padding: 0; }
.ls-pay-item {
  display: flex; align-items: center; gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid #F1EEE9;
}
.ls-pay-item:last-child { border-bottom: 0; }
.ls-pay-item.is-recent { background: #FFFBEA; }
.ls-pay-icon {
  width: 48px; height: 48px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 20px; flex-shrink: 0;
  background: #F1EEE9;
}
.ls-pay-icon.is-cash         { background: #E2F2F0; }
.ls-pay-icon.is-cc           { background: #E8EFF7; }
.ls-pay-icon.is-store_credit { background: #FFF4DC; }
.ls-pay-icon.is-finance      { background: #EEE6F9; }
.ls-pay-icon.is-tax_exempt   { background: #FBE9E7; }
.ls-pay-item-body { flex: 1 1 auto; min-width: 0; }
.ls-pay-item-method { font-weight: 700; font-size: 17px; color: #221F1C; }
.ls-pay-item-date { font-size: 14.5px; color: #736C63; margin-top: 3px; }
.ls-pay-item-tags { display: flex; gap: 6px; margin-top: 7px; flex-wrap: wrap; }
.ls-pay-item-amount {
  font-family: 'Sora', sans-serif;
  font-size: 21px; font-weight: 800;
  text-align: right; white-space: nowrap;
}
.ls-pay-item-amount.is-teal { color: #0F6E6B; }
.ls-pay-item-amount.is-danger { color: #C93C30; }

@media (max-width: 767px) {
  .ls-pay-modal-head { grid-template-columns: 1fr; }
}

/* ===================================================== */
/* REDUCED MOTION                                        */
/* ===================================================== */
@media (prefers-reduced-motion: reduce) {
  .ls-row, .ls-logo--lg, .ls-count-pill { animation: none !important; }
}
</style>


// WEBPACK FOOTER //
// src/components/Sales/ListOtherSales.vue